import React, { useState, useEffect, useRef, useContext } from "react";
import MicRecorder from "mic-recorder-to-mp3";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import { Storage, API, graphqlOperation, Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import MicNoneRoundedIcon from "@mui/icons-material/MicNoneRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { getUser } from "../graphql/queries";
import {
  createDailyAnsweredQuestions,
  updateDailyAnsweredQuestions,
  updateUser
} from "../graphql/mutations";
import { listDailyAnsweredQuestions } from "../graphql/queries";
import Feedback from "./Feedback";
import UserContext from "./UserContext";
import { primaryButtonSx, inputSx } from "./PracticePageLayout";
import { buildApiHeaders } from "./apiHeaders";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const CLIENT_ID_KEY = "interview_lab_client_id";

const getOrCreateClientId = () => {
  try {
    const existing = window.localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `cid_${Date.now().toString(36)}_${Math.random()
            .toString(36)
            .slice(2, 10)}`;
    window.localStorage.setItem(CLIENT_ID_KEY, fresh);
    return fresh;
  } catch (e) {
    return "anon";
  }
};

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const SURFACE_MUTED = "rgba(15, 17, 33, 0.025)";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const formatTime = secs => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(
    s % 60
  ).padStart(2, "0")}`;
};

const SkeletonLine = ({ w = "100%", h = 12, mb = 1.25 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: "6px",
      mb,
      background:
        "linear-gradient(90deg, rgba(15, 17, 33, 0.05) 0%, rgba(15, 17, 33, 0.11) 50%, rgba(15, 17, 33, 0.05) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s ease-in-out infinite",
      "@keyframes skeletonShimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" }
      }
    }}
  />
);

const StageLabel = ({ index, label, active, done }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      py: 0.5,
      px: 1,
      borderRadius: "999px",
      border: `1px solid ${active || done ? INK : INK_HAIRLINE}`,
      backgroundColor: active ? INK : "transparent",
      color: active ? "#fafaf7" : done ? INK : INK_FAINT,
      transition: "all 0.2s ease"
    }}
  >
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: "0.66rem",
        fontWeight: 500,
        letterSpacing: "0.12em",
        fontVariantNumeric: "tabular-nums"
      }}
    >
      {String(index).padStart(2, "0")}
    </Typography>
    <Typography
      sx={{
        fontFamily: SANS,
        fontSize: "0.74rem",
        fontWeight: 500,
        letterSpacing: "0.04em",
        textTransform: "uppercase"
      }}
    >
      {label}
    </Typography>
  </Box>
);

const MiniPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().catch(() => {});
      setPlaying(true);
    }
  };

  const seek = e => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = (Number(e.target.value) / 100) * duration;
  };

  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        border: `1px solid ${INK_HAIRLINE}`,
        borderRadius: "14px",
        backgroundColor: "#fff"
      }}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        style={{ display: "none" }}
      />
      <Box
        component="button"
        onClick={toggle}
        sx={{
          appearance: "none",
          cursor: "pointer",
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "none",
          backgroundColor: INK,
          color: "#fafaf7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.15s ease",
          "&:hover": { transform: "translateY(-1px)" },
          "&:active": { transform: "scale(0.96)" }
        }}
      >
        {playing ? (
          <PauseRoundedIcon sx={{ fontSize: 20 }} />
        ) : (
          <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, position: "relative", height: 16 }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 3,
            transform: "translateY(-50%)",
            backgroundColor: INK_HAIRLINE,
            borderRadius: "999px"
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            width: `${pct}%`,
            height: 3,
            transform: "translateY(-50%)",
            backgroundColor: INK,
            borderRadius: "999px",
            transition: "width 0.1s linear"
          }}
        />
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={pct}
          onChange={seek}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            margin: 0,
            padding: 0
          }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: "0.74rem",
          color: INK_SOFT,
          fontVariantNumeric: "tabular-nums",
          minWidth: 92,
          textAlign: "right"
        }}
      >
        {formatTime(time)} / {formatTime(duration)}
      </Typography>
    </Box>
  );
};

const AudioRecorder = ({
  question,
  conversation,
  onConversationTextChange,
  blobURL,
  audioKey,
  responseText,
  feedback,
  feedbackGeneratedAt,
  onBlobURLChange,
  onAudioKeyChange,
  onResponseTextChange,
  onFeedbackChange
}) => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [block, setBlock] = useState(null);
  const [transcribed, setTranscribed] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [freshFeedbackAt, setFreshFeedbackAt] = useState(null);
  const { user } = useContext(UserContext);

  useEffect(() => {
    if (feedback && !freshFeedbackAt && !feedbackGeneratedAt) {
      setFreshFeedbackAt(new Date());
    }
    if (!feedback && freshFeedbackAt) {
      setFreshFeedbackAt(null);
    }
  }, [feedback, freshFeedbackAt, feedbackGeneratedAt]);

  useEffect(() => {
    if (!isRecording) {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 200);
    return () => clearInterval(id);
  }, [isRecording]);

  const startRecording = () => {
    Mp3Recorder.start()
      .then(() => setIsRecording(true))
      .catch(e => console.error(e));
  };
  const stopRecording = () => {
    Mp3Recorder.stop()
      .getMp3()
      .then(([buffer, blob]) => {
        const file = new File([blob], "recording.mp3", { type: "audio/mp3" });
        onBlobURLChange(URL.createObjectURL(file));
        setIsRecording(false);
        setIsUploading(true);
        const key = `recording_${Date.now()}.mp3`;
        Storage.put(key, file, { contentType: "audio/mp3" })
          .then(data => {
            onAudioKeyChange(data.key);
          })
          .catch(e => console.log(e))
          .finally(() => setIsUploading(false));
      })
      .catch(e => {
        console.log(e);
        setIsRecording(false);
      });
  };
  const toggleMic = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setTranscribed(true);
    try {
      const response = await API.post("chatEndpoints", "/chat/recording", {
        body: { audioKey, clientId: getOrCreateClientId() },
        headers: await buildApiHeaders(),
        response: true
      });
      const data =
        response && response.data ? response.data : response;
      if (data && data.error === "anon_limit") {
        setBlock("auth");
        setTranscribed(false);
        return;
      }
      if (data && typeof data.text === "string") {
        onResponseTextChange(data.text);
      }
    } catch (error) {
      const status =
        error && error.response && error.response.status;
      const payload =
        error && error.response && error.response.data;
      if (status === 429 || (payload && payload.error === "anon_limit")) {
        setBlock("auth");
        setTranscribed(false);
      } else {
        console.error("Error:", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDailyAnsweredQuestionsData = async (userId, date) => {
    try {
      const response = await API.graphql(
        graphqlOperation(listDailyAnsweredQuestions, {
          filter: {
            userID: { eq: userId },
            date: { eq: date }
          }
        })
      );
      const existing = response.data.listDailyAnsweredQuestions.items;
      if (existing.length > 0) {
        await API.graphql(
          graphqlOperation(updateDailyAnsweredQuestions, {
            input: {
              id: existing[0].id,
              answeredQuestionsCount: existing[0].answeredQuestionsCount + 1
            }
          })
        );
      } else {
        await API.graphql(
          graphqlOperation(createDailyAnsweredQuestions, {
            input: {
              date,
              answeredQuestionsCount: 1,
              userID: userId
            }
          })
        );
      }
    } catch (error) {
      console.error("Error updating daily answered questions:", error);
    }
  };

  const FREE_DAILY_LIMIT = 10;

  async function updateDailyQueries() {
    try {
      const userData = await Auth.currentAuthenticatedUser();
      const username = userData.username;
      const userResponse = await API.graphql(
        graphqlOperation(getUser, { id: username })
      );
      const u = userResponse.data.getUser;
      if (!u) return false;
      if (u.subscriptionType === "PREMIUM") return false;
      if (u.subscriptionType === "FREE") {
        const today = new Date().toISOString().split("T")[0];
        const daqResponse = await API.graphql(
          graphqlOperation(listDailyAnsweredQuestions, {
            filter: {
              userID: { eq: username },
              date: { eq: today }
            }
          })
        );
        const todayItem =
          daqResponse.data.listDailyAnsweredQuestions.items[0];
        const todayCount = todayItem ? todayItem.answeredQuestionsCount : 0;
        return todayCount >= FREE_DAILY_LIMIT;
      }
      return false;
    } catch (error) {
      console.error("Error checking daily limit:", error);
      return false;
    }
  }

  const handleSubmitResponse = async e => {
    e.preventDefault();
    const isAnonymous = !user || !user.username;

    if (!isAnonymous) {
      setIsCompleting(true);
      if (await updateDailyQueries()) {
        setBlock("limit");
        setIsCompleting(false);
        return;
      }
      const dateString = new Date().toISOString().split("T")[0];
      await updateDailyAnsweredQuestionsData(user.username, dateString);
    }

    setIsCompleting(true);
    let questionResponse = `Task: Act as an interviewing assistant and provide informative and constructive feedback, on the following response to the interview question:
    Question: ${question}
    Response: "${responseText}"
    Topic: Interviewing Feedback
    Style: Business
    Tone: Professional
    Audience: Business audience
    Please format your feedback in the following JSON format:

    {
      "feedback": {
        "positive": [
          // In-depth list of things done well in the response
        ],
        "constructive": [
          // In-depth list of things that were not done well in the response
        ],
        "improvement": [
          // In-depth list of ways that the interviewee can improve their response
        ]
      }
    }

    If there is nothing to say about a particular field, the array must be left empty. Please make sure to use the exact field names provided (positive, constructive, and improvement), and include double quotes around all strings. Do not include any additional text in your response.
    `;
    const newConversation = { role: "user", content: questionResponse };
    const baseConversation = Array.isArray(conversation) ? conversation : [];
    try {
      await API.post("chatEndpoints", "/chat/completion", {
        body: [...baseConversation, newConversation],
        headers: await buildApiHeaders()
      }).then(res => {
        const result = res.query.content;
        if (typeof onConversationTextChange === "function") {
          onConversationTextChange([newConversation, res.query]);
        }
        const cleanedResult = result.startsWith("\n\n")
          ? result.substring(2)
          : result;
        const json = JSON.parse(cleanedResult);
        onFeedbackChange(JSON.stringify(json.feedback));
      });
    } catch (error) {
      console.error(error);
      onResponseTextChange("An error occurred. Please try again later.");
    } finally {
      setIsCompleting(false);
    }
  };

  const stageRecord = !blobURL && !audioKey;
  const stageReview = blobURL && !responseText && !transcribed;
  const stageTranscript = transcribed || !!responseText;
  const stageFeedback = !!feedback || isCompleting;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 2.5
        }}
      >
        <StageLabel
          index={1}
          label="Record"
          active={stageRecord || isRecording}
          done={!stageRecord}
        />
        <StageLabel
          index={2}
          label="Transcribe"
          active={stageReview || isSubmitting}
          done={stageTranscript}
        />
        <StageLabel
          index={3}
          label="Feedback"
          active={isCompleting}
          done={!!feedback}
        />
      </Box>

      <Box
        sx={{
          border: `1px solid ${INK_HAIRLINE}`,
          backgroundColor: SURFACE_MUTED,
          borderRadius: "18px",
          p: { xs: 2.5, sm: 3 },
          mb: stageReview || stageTranscript || stageFeedback ? 2.5 : 0
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ position: "relative", width: 64, height: 64 }}>
              {isRecording && (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    inset: -6,
                    borderRadius: "50%",
                    border: "2px solid rgba(56, 84, 232, 0.45)",
                    animation: "micPulse 1.6s ease-out infinite",
                    "@keyframes micPulse": {
                      "0%": { transform: "scale(0.85)", opacity: 0.9 },
                      "100%": { transform: "scale(1.4)", opacity: 0 }
                    }
                  }}
                />
              )}
              <Box
                component="button"
                onClick={toggleMic}
                disabled={isUploading}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  appearance: "none",
                  cursor: isUploading ? "not-allowed" : "pointer",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: isRecording ? "#d4264a" : INK,
                  color: "#fafaf7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition:
                    "transform 0.15s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                  boxShadow: isRecording
                    ? "0 12px 28px -10px rgba(212, 38, 74, 0.5)"
                    : "0 12px 28px -14px rgba(15, 17, 33, 0.55)",
                  "&:hover": {
                    transform: isUploading ? "none" : "translateY(-1px)"
                  },
                  "&:active": {
                    transform: isUploading ? "none" : "scale(0.97)"
                  },
                  "&:disabled": { opacity: 0.55 }
                }}
              >
                {isRecording ? (
                  <StopRoundedIcon sx={{ fontSize: 28 }} />
                ) : (
                  <MicNoneRoundedIcon sx={{ fontSize: 28 }} />
                )}
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: INK,
                  lineHeight: 1.3
                }}
              >
                {isRecording
                  ? "Recording…"
                  : isUploading
                  ? "Uploading take…"
                  : blobURL
                  ? "Take saved"
                  : "Tap to record your answer"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.78rem",
                  letterSpacing: "0.06em",
                  color: INK_FAINT,
                  fontVariantNumeric: "tabular-nums",
                  mt: 0.5
                }}
              >
                {isRecording
                  ? formatTime(elapsed)
                  : blobURL
                  ? "Re-tap mic to redo"
                  : "Up to a few minutes per answer"}
              </Typography>
            </Box>
          </Box>
          {blobURL && !isRecording && (
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <MiniPlayer src={blobURL} />
            </Box>
          )}
        </Box>
      </Box>

      {(stageReview || stageTranscript) && (
        <Box sx={{ mb: stageFeedback ? 2.5 : 0 }}>
          {isSubmitting ? (
            <Box
              sx={{
                border: `1px solid ${INK_HAIRLINE}`,
                borderRadius: "16px",
                p: 2.5,
                backgroundColor: "#fff"
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT,
                  mb: 1.5
                }}
              >
                Transcribing your take
              </Typography>
              <SkeletonLine w="92%" />
              <SkeletonLine w="78%" />
              <SkeletonLine w="86%" mb={0} />
            </Box>
          ) : stageTranscript ? (
            <Box>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_FAINT,
                  mb: 1
                }}
              >
                Transcript — edit before sending
              </Typography>
              <TextField
                value={responseText}
                multiline
                minRows={4}
                onChange={e => onResponseTextChange(e.target.value)}
                placeholder="Your transcript will appear here. Tweak any words the model mis-heard."
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  variant="contained"
                  onClick={handleSubmitResponse}
                  disabled={!responseText || isCompleting}
                  startIcon={
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
                  }
                  sx={primaryButtonSx}
                >
                  {isCompleting ? "Analyzing…" : "Get written feedback"}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button
                variant="contained"
                disabled={!audioKey || isUploading}
                onClick={handleSubmit}
                sx={primaryButtonSx}
              >
                {isUploading ? "Uploading…" : "Transcribe answer"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {isCompleting && !feedback && (
        <Box
          sx={{
            border: `1px solid ${INK_HAIRLINE}`,
            borderRadius: "16px",
            p: 2.5,
            backgroundColor: "#fff"
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.7rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: INK_FAINT,
              mb: 1.5
            }}
          >
            Reading your answer
          </Typography>
          <SkeletonLine w="40%" h={10} />
          <SkeletonLine w="92%" />
          <SkeletonLine w="84%" />
          <SkeletonLine w="40%" h={10} />
          <SkeletonLine w="88%" />
          <SkeletonLine w="74%" mb={0} />
        </Box>
      )}

      {feedback && !isCompleting && (
        <Box>
          <Feedback
            feedback={feedback}
            generatedAt={feedbackGeneratedAt || freshFeedbackAt}
          />
          {(!user || !user.username) && (
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: "16px",
                border: `1px dashed rgba(56, 84, 232, 0.4)`,
                backgroundColor: "rgba(56, 84, 232, 0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: SANS,
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    color: INK
                  }}
                >
                  That was your free analysis.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: SANS,
                    fontSize: "0.88rem",
                    color: INK_SOFT,
                    mt: 0.25
                  }}
                >
                  Sign in to keep practicing — unlimited feedback, saved
                  sessions, progress tracking.
                </Typography>
              </Box>
              <Button
                onClick={() => navigate("/login")}
                variant="contained"
                sx={{
                  ...primaryButtonSx,
                  py: 0.8,
                  px: 2.25,
                  fontSize: "0.88rem"
                }}
              >
                Create account
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={!!block} onClose={() => setBlock(null)}>
        <DialogTitle>
          {block === "auth"
            ? "Want feedback like that on every answer?"
            : "Daily limit reached"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {block === "auth"
              ? "You've used your free analysis. Create an account to keep practicing — unlimited feedback, saved sessions, and progress tracking."
              : "You've hit your 10 free analyses for today. Upgrade to Pro for unlimited submissions, or come back tomorrow for a fresh batch."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlock(null)}>Close</Button>
          <Button
            onClick={() =>
              navigate(block === "auth" ? "/login" : "/upgrade")
            }
            sx={{ ...primaryButtonSx, py: 0.8, px: 2.25, fontSize: "0.88rem" }}
            variant="contained"
          >
            {block === "auth" ? "Create account" : "Upgrade"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioRecorder;
